import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  useCreateFeedbackMutation,
  useUpdateFeedbackMutation,
  useGetAdminFeedbacksQuery,
} from "@/store/api/homepageApi";
import FileUploadButton from "@/components/common/FileUploadButton";
import { uploadMediaFiles } from "@/utils/cloudinaryUpload";

const validationSchema = Yup.object({
  studentName: Yup.string()
    .required("Student name is required")
    .max(100, "Student name must be less than 100 characters"),
  feedbackText: Yup.string()
    .required("Feedback text is required")
    .max(2000, "Feedback text must be less than 2000 characters"),
  scoreImage: Yup.string().url("Must be a valid URL").nullable(),
  displayOrder: Yup.number().min(0, "Display order must be 0 or greater"),
});

const FeedbackForm = ({ open, onClose, feedback, onSuccess }) => {
  const [createFeedback, { isLoading: isCreating }] = useCreateFeedbackMutation();
  const [updateFeedback, { isLoading: isUpdating }] = useUpdateFeedbackMutation();
  const { data: feedbacksData } = useGetAdminFeedbacksQuery({});

  const isEditMode = Boolean(feedback);
  const isLoading = isCreating || isUpdating;

  // Calculate next display order for new feedbacks
  const getNextDisplayOrder = () => {
    if (!feedbacksData?.data || feedbacksData.data.length === 0) {
      return 0;
    }
    const maxOrder = Math.max(...feedbacksData.data.map(fb => fb.displayOrder || 0));
    return maxOrder + 1;
  };

  const formik = useFormik({
    initialValues: {
      studentName: feedback?.studentName || "",
      feedbackText: feedback?.feedbackText || "",
      scoreImage: feedback?.scoreImage || "",
      scoreImageFile: null,
      isActive: feedback?.isActive ?? true,
      displayOrder: feedback?.displayOrder ?? (isEditMode ? 0 : getNextDisplayOrder()),
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        // Upload files first
        const mediaFiles = {};
        if (values.scoreImageFile) {
          mediaFiles.scoreImage = values.scoreImageFile;
        }

        let uploadedUrls = {};
        if (Object.keys(mediaFiles).length > 0) {
          uploadedUrls = await uploadMediaFiles(mediaFiles, "toeic/homepage/feedbacks");
        }

        const payload = {
          studentName: values.studentName,
          feedbackText: values.feedbackText,
          scoreImage: uploadedUrls.scoreImageUrl || values.scoreImage || undefined,
          isActive: values.isActive,
          displayOrder: values.displayOrder,
        };

        if (isEditMode) {
          await updateFeedback({ id: feedback._id, ...payload }).unwrap();
          toast.success("Feedback updated successfully");
        } else {
          await createFeedback(payload).unwrap();
          toast.success("Feedback created successfully");
        }
        
        onSuccess();
        handleClose();
      } catch (error) {
        toast.error(error?.data?.message || "Failed to save feedback");
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const handleScoreImageFileChange = (data) => {
    const file = data?.file || data;
    if (file) {
      formik.setFieldValue("scoreImageFile", file);
      formik.setFieldValue("scoreImage", null); // Clear existing URL
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? "Edit Feedback" : "Create Feedback"}
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Student Name *"
              name="studentName"
              value={formik.values.studentName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.studentName && Boolean(formik.errors.studentName)}
              helperText={formik.touched.studentName && formik.errors.studentName}
            />

            <TextField
              fullWidth
              label="Feedback Text *"
              name="feedbackText"
              multiline
              rows={4}
              value={formik.values.feedbackText}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.feedbackText && Boolean(formik.errors.feedbackText)}
              helperText={formik.touched.feedbackText && formik.errors.feedbackText}
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Score Image (Optional)
              </Typography>
              <FileUploadButton
                onFileSelect={handleScoreImageFileChange}
                accept="image/*"
                label={formik.values.scoreImageFile?.name || "Upload Score Image"}
                currentFile={formik.values.scoreImageFile || formik.values.scoreImage}
                previewUrl={formik.values.scoreImage}
                name="scoreImage"
              />
            </Box>

            
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.isActive}
                  onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
                  name="isActive"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FeedbackForm;
